"use client";

import Image from "next/image";
import { BrandInput } from "@/components/brand/brand-input";
import { Button } from "@/components/ui/button";
import { useState, useEffect, useRef, useCallback } from "react";
import { SlidersHorizontal, Search, SearchX, LibraryBig } from "lucide-react";
import Link from "next/link";
import { CatalogEmptyState } from "@/app/(landing)/_components/catalog/catalog-empty-state";
import { InfiniteScrollLoader } from "@/app/(landing)/_components/catalog/infinite-scroll-loader";
import { BottomSheet } from "@/app/(landing)/_components/catalog/bottom-sheet";
import { useInfiniteScroll } from "@/hooks/use-infinite-scroll";
import { useCatalogCategories } from "@/hooks/catalog/use-catalog-categories";
import {
  QuizCard,
  type QuizCardData,
} from "@/app/(landing)/_components/catalog/quiz-card";
import { ExamListSkeleton } from "@/app/(landing)/_components/catalog/exam-list-skeleton";
import { getPublicExamListAction } from "@/server/catalog/actions";
import { Skeleton } from "@/components/ui/skeleton";
import { SidebarFilter } from "@/app/(landing)/_components/catalog/sidebar-filter";
import type { ExamCatalogEntry } from "@/types/exam-catalog";

/**
 * Katalog ujian PUBLIK (zona landing) — pasangan antar-zona:
 * `(user)/_components/catalog/my-exams-section.tsx` (duplikasi penuh
 * disengaja 2026-09-26: zona mandiri, perbaikan UI 2×; jaga penamaan &
 * struktur paritas agar mudah disandingkan).
 *
 * Spesialisasi publik: fetch anonim `getPublicExamListAction` TANPA auth —
 * zero logic kepemilikan (tanpa purchasedIds, tanpa filter "Status",
 * tanpa varian hub sub-kategori).
 */

interface CategoryWithSubs {
  id: string;
  name: string;
  slug: string;
  thumbnail?: string | null;
  icon?: string | null;
  subCategories: { id: string; name: string; slug: string }[];
}

/** Palet aksen tile kategori — identik kartu "Kategori Populer" dashboard
 *  user (referensi yang disetujui; permintaan 2026-09-25). */
const CATEGORY_ACCENTS = [
  // Pasangan foreground per latar (audit warna 2026-09-25): eks text-white
  // di atas bg-secondary/accent (kuning terang ~1.6:1) → token *-foreground.
  { bg: "bg-primary/10", border: "border-primary", text: "text-primary" },
  { bg: "bg-secondary", border: "border-secondary", text: "text-secondary-foreground" },
  { bg: "bg-accent", border: "border-accent/30", text: "text-accent-foreground" },
  { bg: "bg-primary", border: "border-primary/30", text: "text-primary-foreground" },
  { bg: "bg-warning/15", border: "border-warning/40", text: "text-warning" },
  { bg: "bg-destructive/15", border: "border-destructive/40", text: "text-destructive" },
];

const PAGE_SIZE = 6;

const MobileFilter = ({
  selected,
  onToggle,
  filterSections,
}: {
  selected: Record<string, string[]>;
  onToggle: (section: string, option: string) => void;
  filterSections: { title: string; options: string[] }[];
}) => {
  const sections = filterSections.filter((s) => s.options.length > 0);

  return (
    <div className="flex flex-col gap-5">
      {sections.map((section) => (
        <div key={section.title}>
          <h4 className="text-foreground mb-2.5 text-sm font-bold">{section.title}</h4>
          <div className="flex flex-wrap gap-2">
            {section.options.map((option) => {
              const isActive = selected[section.title]?.includes(option);
              return (
                <Button
                  key={option}
                  type="button"
                  variant={isActive ? "default" : "outline"}
                  size="lg"
                  onClick={() => onToggle(section.title, option)}
                  className="rounded-full"
                >
                  {option}
                </Button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

interface PublicExamsSectionProps {
  /** Max items to show (preview mode homepage). Shows "Lihat Semua" button. */
  maxItems?: number;
  /** Base path for exam detail links. Defaults to "/ujian" (public). */
  basePath?: string;
  /** Data awal dari Server Component (SSR/SEO) — skip fetch mount pertama. */
  initialExams?: ExamCatalogEntry[];
  /** Query awal dari URL `?search=` — mengisi state search + debounced. */
  initialSearch?: string;
  /** Sembunyikan heading "Semua Ujian" bila halaman sudah punya H1 sendiri
   *  (mis. /ujian — hindari duplikat H1). */
  showHeading?: boolean;
}

export const PublicExamsSection = ({
  maxItems,
  basePath = "/ujian",
  initialExams,
  initialSearch,
  showHeading = true,
}: PublicExamsSectionProps) => {
  const [search, setSearch] = useState(initialSearch ?? "");
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearch ?? "");
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const [showFloatingFilter, setShowFloatingFilter] = useState(false);

  // SSR initial data (SEO): seeded dari server, fetch mount pertama di-skip.
  const skipInitialFetch = useRef(Boolean(initialExams?.length));
  const [allExams, setAllExams] = useState<ExamCatalogEntry[]>(initialExams ?? []);
  const [isLoading, setIsLoading] = useState(!initialExams?.length);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [categoryOptions, setCategoryOptions] = useState<string[]>([]);
  // Categories via shared TanStack Query cache (catalogKeys.categories) —
  // dedupe dgn konsumen cache lain; preview homepage (maxItems) TIDAK fetch.
  const { data: categoriesData } = useCatalogCategories<CategoryWithSubs>("public", {
    staleTime: 5 * 60_000,
    enabled: !maxItems,
  });
  const categories = categoriesData ?? [];

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  // Fetch exams — aksi anonim publik (search ILIKE di domain bila ada query;
  // tanpa query pakai cache full-list getExamList).
  const fetchExams = useCallback(async () => {
    setIsLoading(true);
    setFetchError(null);
    try {
      const r = await getPublicExamListAction(debouncedSearch || undefined);
      if (!r.ok) throw new Error(r.error.message);
      const exams = r.data as unknown as ExamCatalogEntry[];
      setAllExams(exams);
      const catNames = exams.map((e) => e.category?.name).filter(Boolean) as string[];
      // Include examRule-based categories for exams without a sub-category
      const ruleNames = exams
        .filter((e) => !e.category && e.examRule)
        .map((e) => e.examRule!.toUpperCase());
      setCategoryOptions([...new Set([...catNames, ...ruleNames])]);
    } catch {
      // Skill 3.6: kegagalan fetch TIDAK menyamar jadi "tidak ada ujian" —
      // tampilkan error state + retry.
      setFetchError("Gagal memuat daftar ujian.");
    } finally {
      setIsLoading(false);
    }
  }, [debouncedSearch]);

  // eslint-disable-next-line react-hooks/set-state-in-effect -- data-fetch-on-mount/dep-change: fetchExams is a memoized async loader whose setState calls populate server state; re-running only when fetchExams identity changes
  useEffect(() => {
    if (skipInitialFetch.current) {
      skipInitialFetch.current = false; // hanya mount pertama yang di-skip
      return;
    }
    fetchExams();
  }, [fetchExams]);

  const handleToggleFilter = (section: string, option: string) => {
    setFilters((prev) => {
      const current = prev[section] || [];
      if (section === "Urutkan" || section === "Kategori") {
        // Single-select for Urutkan and Kategori
        const next = current.includes(option) ? [] : [option];
        // When switching category, clear sub-category filter
        if (section === "Kategori") {
          return { ...prev, [section]: next, "Sub-Kategori": [] };
        }
        return { ...prev, [section]: next };
      }
      const next = current.includes(option)
        ? current.filter((o) => o !== option)
        : [...current, option];
      return { ...prev, [section]: next };
    });
    resetScroll();
  };

  const activeFilterCount = Object.values(filters).flat().length;

  // Get active category's sub-categories
  const selectedCategoryName = filters.Kategori?.[0];
  const selectedCategoryData = selectedCategoryName
    ? categories.find((c) => c.name === selectedCategoryName)
    : null;
  const subCategoryChips = selectedCategoryData?.subCategories || [];

  // Build dynamic filter sections. Kategori TIDAK di sidebar — chips kategori
  // (tampil SEMUA breakpoint) jadi pemilih kanoniknya; sidebar hanya
  // pelengkap (Urutkan/Tipe). Zona publik tanpa seksi "Status" (butuh
  // kepemilikan — milik varian user).
  const kategoriOptions =
    categories.length > 0 ? categories.map((c) => c.name) : categoryOptions;
  const filterSections = [
    { title: "Urutkan", options: ["Terbaru", "Terlama"] },
    { title: "Tipe", options: ["Gratis", "Berbayar"] },
  ];

  // Client-side filtering & sorting
  const filteredExams = (() => {
    let result = allExams;
    const selectedCats = filters.Kategori || [];
    const selectedSubs = filters["Sub-Kategori"] || [];
    if (selectedSubs.length > 0) {
      // Sub-category filter — match by name, keep exams without sub-category if they match the parent category
      result = result.filter((e) => {
        if (e.subCategory?.name && selectedSubs.includes(e.subCategory.name)) return true;
        if (!e.subCategory && e.category?.name && selectedCats.includes(e.category.name))
          return true;
        return false;
      });
    } else if (selectedCats.length > 0) {
      // Category filter — match by category name (covers both subCategory.category and examRule-resolved category)
      result = result.filter((e) => {
        if (e.category?.name && selectedCats.includes(e.category.name)) return true;
        return false;
      });
    }
    const types = filters["Tipe"] || [];
    if (types.length > 0) {
      result = result.filter((e) => {
        if (types.includes("Gratis") && e.type === "free") return true;
        if (types.includes("Berbayar") && (e.type === "paid" || e.type === "premium"))
          return true;
        return false;
      });
    }
    const sorts = filters["Urutkan"] || [];
    if (sorts.includes("Terlama")) {
      result = [...result].reverse();
    }
    return result;
  })();

  // Map to QuizCardData — zona publik: isPurchased selalu false (zero auth).
  const quizCards: QuizCardData[] = filteredExams.map((e) => ({
    id: e.id,
    title: e.title,
    slug: e.slug,
    category: e.category?.name || "",
    subCategory: e.subCategory?.name || "",
    level: "",
    totalQuestions: e.totalQuestions,
    duration: `${e.durationMinutes} menit`,
    rating: e.avgRating || 0,
    totalReviews: e.totalReviews || 0,
    totalParticipants: e.totalParticipants || 0,
    image: e.thumbnail || "",
    price: e.type === "free" ? 0 : (e.discountPrice ?? e.price),
    originalPrice: e.discountPrice && e.discountPrice < e.price ? e.price : undefined,
    isPurchased: false,
  }));

  const isPreview = !!maxItems;
  const displayCards = isPreview ? quizCards.slice(0, maxItems) : quizCards;
  const hasMore = isPreview && quizCards.length > maxItems;

  const {
    visibleCount,
    isLoadingMore,
    hasMore: canLoadMore,
    loadMoreRef,
    reset: resetScroll,
  } = useInfiniteScroll({
    totalCount: isPreview ? displayCards.length : quizCards.length,
    pageSize: isPreview ? maxItems : PAGE_SIZE,
  });
  const visibleQuizzes = isPreview ? displayCards : quizCards.slice(0, visibleCount);

  // Reset pagination HANYA saat kriteria berubah — `resetScroll` stabil
  // (useCallback per pageSize), jadi tidak memicu loop reset saat load-more.
  useEffect(() => {
    resetScroll();
  }, [debouncedSearch, filters, resetScroll]);
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setShowFloatingFilter(entry?.isIntersecting ?? false),
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <section ref={sectionRef}>
        {showHeading && (
          <div className="anim-enter-left mb-4 flex items-center gap-2.5">
            {/* Tier section-header tunggal — tile primary 36px + text-lg
                (redesign 2026-09-25). */}
            <div className="bg-primary/10 text-primary flex h-9 w-9 items-center justify-center rounded-xl">
              <LibraryBig size={17} />
            </div>
            <h2 className="text-foreground text-lg font-extrabold">Semua Ujian</h2>
          </div>
        )}

        {/* Kartu kategori — GAYA KARTU "Kategori Populer" dashboard user
            (referensi yang disetujui, 2026-09-25): tile aksen 48px + nama +
            jumlah ujian, palet bergilir, hover lift. Pemilih kategori
            KANONIK semua breakpoint; seksi Kategori keluar dari sidebar. */}
        {kategoriOptions.length > 0 && (
          <div className="mb-5 grid grid-cols-3 gap-3 sm:grid-cols-4 xl:grid-cols-6">
            {[
              {
                name: "Semua",
                count: allExams.length,
                img: "" as string | null,
                active: !filters.Kategori?.length,
              },
              ...categories.map((cat) => ({
                name: cat.name,
                count: allExams.filter((e) => e.category?.name === cat.name).length,
                img: cat.thumbnail || cat.icon || null,
                active: filters.Kategori?.includes(cat.name),
              })),
            ].map(({ name, count, img, active }, i) => {
              const accent = CATEGORY_ACCENTS[i % CATEGORY_ACCENTS.length]!;
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => {
                    setFilters((prev) => ({
                      ...prev,
                      Kategori: active || name === "Semua" ? [] : [name],
                      "Sub-Kategori": [],
                    }));
                    resetScroll();
                  }}
                  className={`group border-border bg-card hover:border-primary/40 flex flex-col items-center gap-2 rounded-2xl border p-3 text-center transition-all duration-200 hover:-translate-y-1 ${
                    active ? "border-primary/60" : ""
                  }`}
                >
                  <div
                    className={`flex h-12 w-12 items-center justify-center rounded-xl border-2 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-[-6deg] ${accent.border} ${accent.bg}`}
                  >
                    {img ? (
                      <div className="relative h-full w-full overflow-hidden rounded-lg">
                        <Image
                          src={img}
                          alt={name}
                          fill
                          sizes="48px"
                          className="object-cover"
                        />
                      </div>
                    ) : (
                      <span className={`text-lg font-black ${accent.text}`}>
                        {name.charAt(0)}
                      </span>
                    )}
                  </div>
                  <p className="text-foreground line-clamp-2 w-full text-xs leading-tight font-extrabold">
                    {name}
                  </p>
                  <span className="text-foreground/50 text-3xs mt-auto font-bold">
                    {count} ujian
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* Sub-category chips when a category is selected */}
        {subCategoryChips.length > 0 && (
          <div className="mb-5 flex flex-wrap gap-1.5">
            {subCategoryChips.map((sub) => {
              const isActive = filters["Sub-Kategori"]?.includes(sub.name);
              return (
                <Button
                  key={sub.id}
                  type="button"
                  variant={isActive ? "default" : "outline"}
                  size="lg"
                  onClick={() => handleToggleFilter("Sub-Kategori", sub.name)}
                  className="text-2xs rounded-full"
                >
                  {sub.name}
                </Button>
              );
            })}
          </div>
        )}

        <div className="flex gap-8">
          <SidebarFilter
            selected={filters}
            onToggle={handleToggleFilter}
            search={search}
            onSearchChange={setSearch}
            sections={filterSections}
          />

          <div className="flex-1">
            {fetchError ? (
              <div className="border-border/60 bg-card flex flex-col items-center gap-3 rounded-2xl border px-6 py-16 text-center">
                <p className="text-foreground/60 text-sm font-medium">{fetchError}</p>
                <Button type="button" onClick={() => fetchExams()}>
                  Coba lagi
                </Button>
              </div>
            ) : isLoading ? (
              <div>
                <Skeleton className="mb-4 h-5 w-48 rounded-lg" />
                <ExamListSkeleton count={isPreview ? Math.min(maxItems, 6) : 6} />
              </div>
            ) : (
              <>
                {visibleQuizzes.length > 0 ? (
                  <>
                    <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-3">
                      {visibleQuizzes.map((quiz, i) => (
                        <QuizCard
                          key={quiz.id}
                          quiz={quiz}
                          index={i}
                          basePath={basePath}
                        />
                      ))}
                    </div>
                    {isPreview && hasMore && (
                      <div className="mt-6 flex justify-center">
                        <Button
                          size="xl"
                          variant="default"
                          render={<Link href="/ujian" />}
                        >
                          Lihat Semua Ujian{" "}
                          <span className="text-base leading-none font-black">→</span>
                        </Button>
                      </div>
                    )}
                    {!isPreview && (
                      // eslint-disable-next-line react-hooks/refs -- passing the RefObject itself to a forwardRef child's `ref` prop (ref-as-prop); not a .current read during render
                      <InfiniteScrollLoader
                        // eslint-disable-next-line react-hooks/refs -- same rule trips on the ref prop line itself
                        ref={loadMoreRef}
                        hasMore={canLoadMore}
                        isLoading={isLoadingMore}
                        loadingText="Memuat ujian lainnya..."
                        endText="Semua ujian sudah ditampilkan"
                        totalCount={quizCards.length}
                        pageSize={PAGE_SIZE}
                      />
                    )}
                  </>
                ) : (
                  <CatalogEmptyState
                    icon={SearchX}
                    title="Ujian tidak ditemukan"
                    subtitle="Coba ubah kata kunci atau filter pencarian"
                  />
                )}
              </>
            )}
          </div>
        </div>
      </section>

      {showFloatingFilter && (
        <Button
          type="button"
          size="xl"
          onClick={() => setMobileFilterOpen(true)}
          className="anim-pop fixed inset-x-0 bottom-24 z-50 mx-auto w-fit rounded-full shadow-lg lg:hidden"
        >
          <SlidersHorizontal size={15} /> Filter & Cari
          {activeFilterCount > 0 && (
            <span className="bg-card text-primary text-3xs flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 font-bold">
              {activeFilterCount}
            </span>
          )}
        </Button>
      )}

      <BottomSheet
        open={mobileFilterOpen}
        onClose={() => setMobileFilterOpen(false)}
        title="Filter & Cari"
      >
        <div className="relative mb-5">
          <div className="text-muted-foreground pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2">
            <Search size={16} />
          </div>
          <BrandInput
            variant="filter"
            type="text"
            placeholder="Cari ujian..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border-muted-foreground h-11 rounded-lg border pr-4 pl-10"
          />
        </div>
        <MobileFilter
          selected={filters}
          onToggle={handleToggleFilter}
          filterSections={filterSections}
        />
        <Button
          size="xl"
          type="button"
          onClick={() => setMobileFilterOpen(false)}
          className="mt-6 w-full"
        >
          Terapkan
        </Button>
      </BottomSheet>
    </>
  );
};
